import { adminLogin, logout } from '@/api/user'
import { getUserMenus } from '@/api/menu'
import { setUserInfo, clearUserInfo, hasPermission } from '@/utils/auth'
import { generateRoutes } from '@/utils/dynamicRoutes'

const state = {
  token: sessionStorage.getItem('access-token') || '',
  userInfo: JSON.parse(sessionStorage.getItem('userInfo') || 'null'),
  menuTree: JSON.parse(sessionStorage.getItem('menuTree') || '[]'),
  permissions: JSON.parse(sessionStorage.getItem('permissions') || '[]'),
  routesLoaded: sessionStorage.getItem('routesLoaded') === 'true' || false,
  RolePermissionAllData: JSON.parse(sessionStorage.getItem('RolePermissionAllData') || '[]'),
  RoleAllData: JSON.parse(sessionStorage.getItem('RoleAllData') || '[]'),
  ZoneAllData: JSON.parse(sessionStorage.getItem('ZoneAllData') || '[]'),
  csrfToken: localStorage.getItem('csrfToken') || '',
  refreshToken: localStorage.getItem('refreshToken') || ''
}

const getters = {
  token: state => state.token,
  userInfo: state => state.userInfo,
  menuTree: state => state.menuTree,
  permissions: state => state.permissions,
  RolePermissionAllData: state => state.RolePermissionAllData,
  RoleAllData: state => state.RoleAllData,
  ZoneAllData: state => state.ZoneAllData,
  routesLoaded: state => state.routesLoaded,
  csrfToken: state => state.csrfToken,
  refreshToken: state => state.refreshToken,
  hasPermission: (state) => (permission) => {
    // 使用闭包访问state.permissions
    return hasPermission(permission, state.permissions)
  }
}

const mutations = {
  SET_TOKEN: (state, token) => {
    console.log('设置token:', token ? '有' : '无');
    state.token = token
    if (token) {
      sessionStorage.setItem('access-token', token)
    } else {
      sessionStorage.removeItem('access-token')
    }
  },
  SET_USER_INFO: (state, userInfo) => {
    state.userInfo = userInfo
    if (userInfo) {
      setUserInfo(userInfo)
    } else {
      sessionStorage.removeItem('userInfo')
    }
  },
  SET_MENU_TREE: (state, menuTree) => {
    state.menuTree = menuTree
    sessionStorage.setItem('menuTree', JSON.stringify(menuTree))
  },
  SET_PERMISSIONS: (state, permissions) => {
    state.permissions = permissions
    sessionStorage.setItem('permissions', JSON.stringify(permissions))
  },
  SET_ROUTES_LOADED: (state, loaded) => {
    state.routesLoaded = loaded
    sessionStorage.setItem('routesLoaded', loaded.toString())
  },
  SET_ROLE_PERMISSION_DATA: (state, data) => {
    state.RolePermissionAllData = data
    sessionStorage.setItem('RolePermissionAllData', JSON.stringify(data))
  },
  SET_ROLE_ALL_DATA: (state, data) => {
    state.RoleAllData = data
    sessionStorage.setItem('RoleAllData', JSON.stringify(data))
  },
  SET_ZONE_ALL_DATA: (state, data) => {
    state.ZoneAllData = data
    sessionStorage.setItem('ZoneAllData', JSON.stringify(data))
  },
  SET_CSRF_TOKEN: (state, token) => {
    state.csrfToken = token
    if (token) {
      localStorage.setItem('csrfToken', token)
    } else {
      localStorage.removeItem('csrfToken')
    }
  },
  SET_REFRESH_TOKEN: (state, token) => {
    state.refreshToken = token
    if (token) {
      localStorage.setItem('refreshToken', token)
    } else {
      localStorage.removeItem('refreshToken')
    }
  },
  CLEAR_USER: (state) => {
    console.log('清除用户状态');
    state.token = ''
    state.userInfo = null
    state.menuTree = []
    state.permissions = []
    state.routesLoaded = false
    state.RolePermissionAllData = []
    state.RoleAllData = []
    state.ZoneAllData = []
    state.csrfToken = ''
    state.refreshToken = ''
    
    // 使用统一的清理函数
    clearUserInfo()
    
    // 清理sessionStorage
    sessionStorage.removeItem('access-token')
    sessionStorage.removeItem('userInfo')
    sessionStorage.removeItem('menuTree')
    sessionStorage.removeItem('permissions')
    sessionStorage.removeItem('routesLoaded')
    sessionStorage.removeItem('RolePermissionAllData')
    sessionStorage.removeItem('RoleAllData')
    sessionStorage.removeItem('ZoneAllData')
    
    // 清理localStorage
    localStorage.removeItem('csrfToken')
    localStorage.removeItem('refreshToken')
  },
  RESET_USER: (state) => {
    console.log('重置用户状态');
    // 只重置状态，不清理sessionStorage（由路由守卫处理）
    state.token = ''
    state.userInfo = null
    state.menuTree = []
    state.permissions = []
    state.routesLoaded = false
    state.RolePermissionAllData = []
    state.RoleAllData = []
    state.ZoneAllData = []
    state.csrfToken = ''
    state.refreshToken = ''
  }
}

const actions = {
  // 管理员登录
// store/modules/user.js 中的 login action
async login({ commit }, userInfo) {
  try {
    console.log('🔐 执行登录操作，用户:', userInfo.username);
    
    // 调用登录接口
    const res = await adminLogin(userInfo);
    
    console.log('📥 Vuex接收到的登录响应:', res);
    
    // ✅ 关键修复：检查响应结构
    if (res && res.success === true) {
      const data = res.data;
      
      // 存储到Vuex和sessionStorage
      commit('SET_TOKEN', data.token);
      commit('SET_CSRF_TOKEN', data.csrfToken);
      commit('SET_REFRESH_TOKEN', data.refreshToken);
      commit('SET_USER_INFO', { 
        ...data.userInfo, 
        permissions: data.permissions 
      });
      commit('SET_MENU_TREE', data.menus);
      commit('SET_PERMISSIONS', data.permissions);
      commit('SET_ROLE_PERMISSION_DATA', data.RolePermissionAllData);
      commit('SET_ROLE_ALL_DATA', data.RoleAllData);
      commit('SET_ZONE_ALL_DATA', data.ZoneAllData);
      commit('SET_ROUTES_LOADED', false);
      
      console.log('✅ Vuex登录处理完成，用户:', data.userInfo.username);
      return res; // ✅ 返回完整的响应对象
    } else {
      // 接口返回 success: false
      console.log('❌ 登录失败:', res?.message);
      return Promise.reject(new Error(res?.message || '登录失败'));
    }
  } catch (error) {
    console.error('❌ 登录异常:', error);
    return Promise.reject(error);
  }
},

  
  // 获取用户信息
  async getUserInfo({ commit }) {
    try {
      const userInfo = JSON.parse(sessionStorage.getItem('userInfo') || 'null')
      if (userInfo) {
        commit('SET_USER_INFO', userInfo)
        return userInfo
      }
      return null
    } catch (error) {
      console.error('获取用户信息失败:', error)
      return null
    }
  },

  // 获取用户菜单
  async getUserMenus({ commit }) {
    try {
      const res = await getUserMenus()
      
      if (res.success) {
        commit('SET_MENU_TREE', res.data)
        
        // 生成动态路由
        const routes = generateRoutes(res.data)
        return {
          menus: res.data,
          routes
        }
      }
      
      return Promise.reject(new Error(res.message || '获取菜单失败'))
    } catch (error) {
      return Promise.reject(error)
    }
  },

  // 退出登录
  async logout({ commit }) {
    try {
      console.log('执行登出操作');
      
      // 先清理本地状态
      commit('RESET_USER');
      
      // 尝试调用后端登出接口
      await logout();
      
    } catch (error) {
      console.warn('登出接口调用失败，继续清理本地状态:', error.message);
      // 即使接口失败，也清理本地状态
    } finally {
      // 最终清理
      commit('CLEAR_USER');
      
      // 清除其他可能的存储
      try {
        localStorage.removeItem('menuData');
      } catch (e) {
        console.warn('清理localStorage失败:', e);
      }
      
      console.log('登出完成');
    }
  },

  // 清除用户信息（用于强制清理）
  clearUser({ commit }) {
    commit('CLEAR_USER')
  },
  
  // 重置用户状态（用于初始化）
  resetUser({ commit }) {
    commit('RESET_USER')
  },
  
  // 更新token
  updateToken({ commit }, token) {
    commit('SET_TOKEN', token)
  },
  
  // 更新用户信息
  updateUserInfo({ commit }, userInfo) {
    commit('SET_USER_INFO', userInfo)
  },
  
  // 更新菜单
  updateMenuTree({ commit }, menuTree) {
    commit('SET_MENU_TREE', menuTree)
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}